import React from 'react';
import { Box, Typography } from '@mui/material';
import { Login, Logout, Schedule } from '@mui/icons-material';
import {
  formatAttendanceDateTime,
  formatWorkingMinutes,
  resolveWorkingStatus,
  workingStatusColor,
  workingStatusLabel,
  WorkingStatus,
} from '../../../utils/attendanceDisplay';

type Props = {
  attendanceDate?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  workingStatus?: WorkingStatus | string | null;
  totalMinutes?: number | null;
  workingTime?: number | string | null;
  compact?: boolean;
};

const TimeRow = ({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: string;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '20px 72px 1fr',
      alignItems: 'center',
      columnGap: 1,
      rowGap: 0,
      py: 0.4,
    }}
  >
    <Box sx={{ color: accent, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f3f4f6', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}
    >
      {value}
    </Typography>
  </Box>
);

export const AttendanceTimeBlock: React.FC<Props> = ({
  attendanceDate,
  checkInTime,
  checkOutTime,
  workingStatus,
  totalMinutes,
  workingTime,
}) => {
  const status = resolveWorkingStatus({
    type: 'PRESENT',
    checkInTime,
    checkOutTime,
    attendanceDate,
    workingStatus,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <TimeRow
        icon={<Login sx={{ fontSize: 15 }} />}
        label="Check in"
        value={formatAttendanceDateTime(checkInTime, attendanceDate)}
        accent="#22C55E"
      />
      <TimeRow
        icon={<Logout sx={{ fontSize: 15 }} />}
        label="Check out"
        value={formatAttendanceDateTime(checkOutTime, attendanceDate)}
        accent="#EF4444"
      />
      {(checkInTime || workingStatus) && (
        <TimeRow
          icon={<Schedule sx={{ fontSize: 15 }} />}
          label="Duration"
          value={`${formatWorkingMinutes(totalMinutes, workingTime)} · ${workingStatusLabel[status]}`}
          accent={workingStatusColor[status]}
        />
      )}
    </Box>
  );
};

export const WorkingStatusChip: React.FC<{
  status: WorkingStatus;
  size?: 'small' | 'medium';
}> = ({ status, size = 'small' }) => {
  const color = workingStatusColor[status];
  const label = workingStatusLabel[status];

  if (status === 'N/A') {
    return (
      <Typography variant="caption" sx={{ color: '#6b7280' }}>
        —
      </Typography>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: size === 'small' ? 1.25 : 1.5,
        py: size === 'small' ? 0.35 : 0.5,
        borderRadius: '6px',
        fontSize: size === 'small' ? '0.72rem' : '0.8rem',
        fontWeight: 600,
        color,
        border: `1px solid ${color}55`,
        backgroundColor: `${color}18`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};
